import type { ReactNode } from 'react';
import './muster.css';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * VORBEHALTE (TX3, Auftrag TV5x 18.10.2026; TX-STANDARD S1.2 Nr. 2)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Der Kasten direkt unter dem `PageHeader`: unbestätigte Annahmen, offene
 * Fachfragen an den Eigentümer, alter Raumstand, veralteter Lauf. Mehrere
 * Bahnen bauten ihn je selbst (`hinweis warnung`); hier steht er einmal.
 *
 * - Dieselben Etiketten, Sätze und dieselbe Reihenfolge wie im PDF
 *   (`Tuv.Berichte.Layout.Bausteine.Vorbehaltsbausteine.Vorbehaltskasten`);
 *   beide prüfen das an `vorbehalte.beispiel.json` (S0.5).
 * - Nie nur Farbe: Jede Zeile trägt ihr Etikett als Text.
 * - Ohne Vorbehalt steht nichts da - kein leerer Kasten.
 */

export type Vorbehaltsart = 'annahmen' | 'fachfrage' | 'raumstand' | 'veraltet' | 'sonstiges';

export interface Vorbehalt {
  art: Vorbehaltsart;
  /** Was gilt, als Satz. */
  text: string;
  /** Wo es weitergeht, z. B. „Annahmen und Vorgaben" - erscheint als „(siehe …)". */
  verweis?: string;
  /** Ziel des Verweises auf der Seite (Anker oder Pfad); ohne Ziel bleibt der Verweis Text. */
  ziel?: string;
}

/** Die Überschrift des Kastens - im PDF seine Einleitung. */
export const VORBEHALTE_TITEL = 'Vorbehalte zu diesen Ergebnissen';

const ETIKETT: Record<Vorbehaltsart, string> = {
  annahmen: 'Annahmen',
  fachfrage: 'Fachfrage',
  raumstand: 'Raumstand',
  veraltet: 'Veraltet',
  sonstiges: 'Vorbehalt',
};

const RANG: Record<Vorbehaltsart, number> = {
  annahmen: 0, fachfrage: 1, raumstand: 2, veraltet: 3, sonstiges: 4,
};

/**
 * Der Standardvorbehalt für unbestätigte Annahmen - wortgleich mit dem
 * Warnkasten des Annahmenblatts im PDF (`Pruefbausteine.Annahmenhinweis`).
 */
export function annahmenVorbehalt(
  offen: number,
  gesamt: number,
  verweis: string | undefined = 'Annahmen und Vorgaben',
  ziel?: string,
): Vorbehalt {
  return {
    art: 'annahmen',
    text: `${offen} von ${gesamt} ${gesamt === 1 ? 'Annahme ist' : 'Annahmen sind'} unbestätigt. `
      + 'Solange eine genutzte Annahme unbestätigt ist, bleibt der Bericht Vorabzug (TX-STANDARD S3.5).',
    verweis,
    ziel,
  };
}

/**
 * Die Zeilen in Kastenreihenfolge: nach Art (Annahmen, Fachfrage, Raumstand,
 * Veraltet, Sonstiges), innerhalb einer Art wie übergeben. Leere Texte fallen weg.
 */
export function vorbehaltszeilen(eintraege: readonly Vorbehalt[]): {
  art: Vorbehaltsart; etikett: string; text: string; satz: string; verweis?: string; ziel?: string;
}[] {
  return eintraege
    .filter((v) => v.text.trim() !== '')
    .map((v, i) => ({ v, i }))
    .sort((a, b) => RANG[a.v.art] - RANG[b.v.art] || a.i - b.i)
    .map(({ v }) => {
      const text = v.text.trim();
      const verweis = v.verweis?.trim() || undefined;
      return {
        art: v.art,
        etikett: ETIKETT[v.art],
        text,
        satz: verweis ? `${text} (siehe ${verweis})` : text,
        verweis,
        ziel: v.ziel,
      };
    });
}

export interface VorbehalteProps {
  eintraege: readonly Vorbehalt[];
  /** Zusätzliches unter der Liste (selten; z. B. ein Knopf „Annahmen bestätigen"). */
  zusatz?: ReactNode;
  className?: string;
}

export function Vorbehalte({ eintraege, zusatz, className }: VorbehalteProps) {
  const zeilen = vorbehaltszeilen(eintraege);
  if (zeilen.length === 0) return null;

  return (
    <aside className={`m-vorbehalte${className ? ` ${className}` : ''}`} aria-label={VORBEHALTE_TITEL}>
      <p className="m-vorbehalte-titel">
        <span className="m-vorbehalte-zeichen" aria-hidden="true">!</span>
        {VORBEHALTE_TITEL}
      </p>
      <ul className="m-vorbehalte-liste">
        {zeilen.map((z, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <li key={i} className="m-vorbehalt" data-art={z.art}>
            <span className="m-vorbehalt-etikett">{z.etikett}</span>
            <span className="m-vorbehalt-text">
              {z.text}
              {z.verweis && (
                <>
                  {' '}
                  (siehe
                  {' '}
                  {z.ziel ? <a href={z.ziel}>{z.verweis}</a> : z.verweis}
                  )
                </>
              )}
            </span>
          </li>
        ))}
      </ul>
      {zusatz}
    </aside>
  );
}
